import {
  INTEGRATION_TYPES,
  type IntegrationDefinition,
  type IntegrationEndpointInfo,
} from '../types'

import {
  CayentaUtilityBillingEditableFields,
  DEFAULT_CAYENTA_UTILITY_BILLING_CONFIG,
} from './CayentaUtilityBillingEditableFields'

export { DEFAULT_CAYENTA_UTILITY_BILLING_CONFIG }

const CAYENTA_UTILITY_BILLING_ENDPOINTS: IntegrationEndpointInfo[] = [
  {
    name: 'Find Locations',
    method: 'POST',
    description: 'Search for service locations by address',
    toolName: 'find_locations',
  },
  {
    name: 'Check Balance by Account',
    method: 'POST',
    description: 'Check account balance using account number with SSN verification',
    toolName: 'check_balance_by_account',
  },
  {
    name: 'Check Balance by Location',
    method: 'POST',
    description: 'Check account balance using location number with SSN verification',
    toolName: 'check_balance_by_location',
  },
  {
    name: 'Check Bill Due Date by Account',
    method: 'POST',
    description: 'Check bill due date using account number with SSN verification',
    toolName: 'check_bill_due_date_by_account',
  },
  {
    name: 'Check Bill Due Date by Location',
    method: 'POST',
    description: 'Check bill due date using location number with SSN verification',
    toolName: 'check_bill_due_date_by_location',
  },
  {
    name: 'Check Pay Arrangements by Account',
    method: 'POST',
    description: 'Check payment arrangements using account number with SSN verification',
    toolName: 'check_pay_arrangements_by_account',
  },
  {
    name: 'Check Pay Arrangements by Location',
    method: 'POST',
    description: 'Check payment arrangements using location number with SSN verification',
    toolName: 'check_pay_arrangements_by_location',
  },
  {
    name: 'Check Budget Balance by Account',
    method: 'POST',
    description: 'Check budget balance using account number with SSN verification',
    toolName: 'check_budget_bill_by_account',
  },
  {
    name: 'Check Budget Balance by Location',
    method: 'POST',
    description: 'Check budget balance using location number with SSN verification',
    toolName: 'check_budget_bill_by_location',
  },
]

export const cayentaUtilityBillingIntegration: IntegrationDefinition = {
  id: INTEGRATION_TYPES.CAYENTA_UTILITY_BILLING,
  title: 'Cayenta Utility Billing',
  defaultConnectionName: 'Cayenta Utility Billing',
  logo: 'https://polimorphic-static.s3.us-east-2.amazonaws.com/3p-logos/cayenta-logo.png',
  icon: 'https://polimorphic-static.s3.us-east-2.amazonaws.com/3p-logos/cayenta-icon.png',
  cardDescription:
    'Connect to Cayenta Utility Billing API for account balance lookups, bill due dates, payment arrangements, and budget billing with SSN verification.',
  modalDescription:
    'Connect to the Cayenta Utility Billing API using Basic Auth. The integration supports account lookups by account number or location number with automatic SSN verification for identity protection.',
  formFields: [
    {
      name: 'host',
      label: 'Host',
      placeholder: 'e.g., example.domain.org:123',
      helperText: 'The Cayenta API server hostname and port',
      required: true,
      type: 'url',
    },
    {
      name: 'username',
      label: 'Username',
      placeholder: 'Enter API username',
      helperText: 'Cayenta API username for Basic Auth',
      required: true,
      type: 'text',
    },
    {
      name: 'password',
      label: 'Password',
      placeholder: 'Enter API password',
      helperText: 'Cayenta API password for Basic Auth',
      required: true,
      type: 'password',
    },
  ],
  apiConfig: {
    createEndpoint: '/integrations/generate-cayenta-utility-billing-integration/',
    buildPayload: (formData, config, _organizationId, organizationIdentityId) => ({
      organization_identity_id: organizationIdentityId,
      host: formData.host,
      username: formData.username,
      password: formData.password,
      connection_name: formData.connectionName || 'Cayenta Utility Billing',
      excluded_endpoints: (config.excluded_endpoints as string[]) || [],
    }),
    authTokenName: 'basic',
    buildTestCredentials: (formData) => ({
      host: formData.host,
      username: formData.username,
      password: formData.password,
    }),
  },
  defaultConfig: { ...DEFAULT_CAYENTA_UTILITY_BILLING_CONFIG },
  endpoints: CAYENTA_UTILITY_BILLING_ENDPOINTS,
  EditableFieldsComponent: CayentaUtilityBillingEditableFields,
}
